import { useCallback, useState } from 'react'

import { useNavigate } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { useSourceBusterEvents } from 'src/Hooks/useSourceBusterEvents'
import {
  artistRegistrationCompleteEvent,
  fillingRegisterFormEvent,
  locationEvent,
} from 'src/Helpers/TagManager'
import { signupLoginError } from 'src/Helpers/TagManager/AuthEvents'
import { getLoading } from 'src/Redux/auth-process/credentialsSlice/selectors'
import {
  registerArtist,
  RegisterArtistError,
} from 'src/Redux/auth-process/credentialsSlice/api-actions'
import { createdFirst, feedback } from 'src/Router/routes'
import { getCookieByName, getUtmParameters } from 'src/Constants/functions'
import { persistPendingReferralCodes } from 'src/Utils/pendingReferralCodes'
import { clearPendingGoogleAuth } from 'src/Utils/pendingGoogleAuth'
import { hasSentSignup, markSignupSent } from 'src/Utils/sentSignupUsers'
import { ArtistRolesEnum } from 'src/Constants/enums'

import { FormDataProps } from './index'

export interface UseArtistContactDetails {
  isEmailExist: boolean
  clearEmailExist: () => void
  handleSubmit: (data: FormDataProps, token: string) => Promise<void>
  loading?: boolean
  onStartFillingForm: () => void
}

const HTTP_STATUS_CONFLICT = 409

export const useArtistContactDetails = (): UseArtistContactDetails => {
  const navigate = useNavigate()
  const { search } = useCustomSearchParams()
  const loading = useAppSelector(getLoading)

  const [isEmailExist, setIsEmailExist] = useState(false)

  const dispatch = useAppDispatch()
  const { sourceBusterLoginEvent } = useSourceBusterEvents()

  const onStartFillingForm = useCallback((): void => {
    locationEvent()
    fillingRegisterFormEvent()
  }, [])

  const handleSubmit = useCallback(
    async (data: FormDataProps, token: string): Promise<void> => {
      setIsEmailExist(false)

      const utmObj = getUtmParameters(getCookieByName('soundCampaignUtm'))
      persistPendingReferralCodes({
        internalReferralCode: search?.get('referralCode') || void 0,
        referralCode: utmObj.ref || void 0,
      })

      await dispatch(
        registerArtist({
          artistRole: ArtistRolesEnum.ARTIST,
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          phoneNumber: data.phoneNumber || void 0,
          captchaToken: token,
        }),
      )
        .unwrap()
        .then((res) => {
          clearPendingGoogleAuth()
          if (res.userId) {
            artistRegistrationCompleteEvent({
              platformName: 'without_track',
              userId: res.userId,
              userRole: ArtistRolesEnum.ARTIST,
            })

            if (!hasSentSignup('email_signup', res.userId)) {
              markSignupSent('email_signup', res.userId)
              sourceBusterLoginEvent({
                token: res.token,
                eventName: 'email_signup',
              })
            }
          }

          navigate(`${createdFirst}${feedback}?sc_token=${res.token}`)
        })
        .catch((error: RegisterArtistError) => {
          signupLoginError()

          if (error.status === HTTP_STATUS_CONFLICT) {
            setIsEmailExist(true)
          }
        })
    },
    [dispatch, navigate, search, sourceBusterLoginEvent],
  )

  const clearEmailExist = useCallback((): void => {
    setIsEmailExist(false)
  }, [])

  return {
    isEmailExist,
    clearEmailExist,
    handleSubmit,
    onStartFillingForm,
    loading,
  }
}
